// src/services/alphaVantage/keyManager.ts
import { ApiTier } from './config';

interface ApiKeyConfig {
  key: string;
  tier: ApiTier;
  isActive: boolean;
}

class ApiKeyManager {
  private keys: ApiKeyConfig[] = [];
  private currentKeyIndex: number = 0;

  constructor() {
    // Load keys from environment or config file
    this.loadKeys();
  }

  private loadKeys() {
    // In production, this would load from secure storage
    const apiKeys = [];

    // *** START CHANGE ***
    // Prioritize the primary API key if provided
    const primaryKey = import.meta.env.VITE_ALPHA_VANTAGE_API_KEY;
    if (primaryKey) {
      // Assume primary key is at least BASIC tier, adjust if needed
      apiKeys.push({ key: primaryKey, tier: ApiTier.BASIC, isActive: true });
    }

    // Add the free key as a fallback if different from the primary
    const freeKey = import.meta.env.VITE_ALPHA_VANTAGE_FREE_KEY;
    if (freeKey && freeKey !== primaryKey) {
      apiKeys.push({ key: freeKey, tier: ApiTier.FREE, isActive: true });
    }
    // *** END CHANGE ***

    // Fallback to 'demo' if no keys are found in env
    if (apiKeys.length === 0) {
      console.warn("No Alpha Vantage API keys found in environment variables. Using 'demo' key with limited functionality.");
      apiKeys.push({ key: 'demo', tier: ApiTier.FREE, isActive: true });
    }

    this.keys = apiKeys.filter(k => k.key && k.isActive);

    if (this.keys.length === 0) {
        console.error("CRITICAL: No usable Alpha Vantage API keys configured.");
    } else {
        console.log(`Loaded ${this.keys.length} Alpha Vantage API key(s).`);
    }
  }

  getNextKey(): string {
    if (this.keys.length === 0) {
      // Throw error or return 'demo' key if preferred fallback
      throw new Error('No API keys available');
      // return 'demo';
    }

    // Cycle through active keys only
    let attempts = 0;
    while (attempts < this.keys.length) {
        const keyConfig = this.keys[this.currentKeyIndex];
        this.currentKeyIndex = (this.currentKeyIndex + 1) % this.keys.length;
        if (keyConfig.isActive) {
            // console.log(`Using Alpha Vantage Key ending in ...${keyConfig.key.slice(-4)}`); // Optional: log key usage
            return keyConfig.key;
        }
        attempts++;
    }

    // If all keys are inactive (should ideally not happen with loadKeys logic)
    console.error("All configured Alpha Vantage keys are inactive.");
    throw new Error('All API keys are inactive');
    // return 'demo';
  }

  getTierForKey(key: string): ApiTier {
    const keyConfig = this.keys.find(k => k.key === key);
    return keyConfig?.tier || ApiTier.FREE;
  }

  addKey(key: string, tier: ApiTier): void {
    if (!this.keys.some(k => k.key === key)) {
      this.keys.push({ key, tier, isActive: true });
    }
  }

  disableKey(key: string): void {
    const keyIndex = this.keys.findIndex(k => k.key === key);
    if (keyIndex >= 0) {
       console.warn(`Disabling Alpha Vantage Key ending in ...${key.slice(-4)} due to potential rate limit.`);
       this.keys[keyIndex].isActive = false;
       // Attempt to find another active key immediately
       this.currentKeyIndex = this.keys.findIndex(k => k.isActive);
       if (this.currentKeyIndex === -1) {
           this.currentKeyIndex = 0; // Reset if all are inactive
           console.error("All Alpha Vantage keys have been disabled.");
       }
    }
  }
}

export const apiKeyManager = new ApiKeyManager();
